import { TrendingUp, Trophy, Plus } from "lucide-react";
import { getIconByName } from "@/components/team/performanceMetricsConfig";
import type { TeamPerformanceMetric, TeamMemberPerformance } from "@/types/team";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import TaskWidget from "@/components/TaskWidget";
import { HomeTodos } from "@/components/HomeTodos";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from '@/components/NotificationBell';
import { D2DFollowUpCalendar } from "@/components/d2d/D2DFollowUpCalendar";
import { AppAssistant } from "@/components/AppAssistant";

// Shared components
import { DashboardHeader } from "@/components/shared/DashboardHeader";
import { useDashboardData } from "@/hooks/useDashboardData";
import { PerformanceCard } from "@/components/home/PerformanceCard";
import { TodoOverviewCards } from "@/components/home/TodoOverviewCards";
import { QuickActions } from "@/components/home/QuickActions";

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [todos, setTodos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<TeamPerformanceMetric[]>([]);
  const [performances, setPerformances] = useState<TeamMemberPerformance[]>([]);

  // Use shared dashboard data hook
  const {
    currentTime,
    todayFollowUps,
    globalLeadCounts,
    isLoadingLeadCounts,
    activeArea
  } = useDashboardData();

  // Initialize sidebar state from localStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Fetch user profile - MUST be before any early returns to maintain hooks order
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('profiles')
        .select('first_name, team_id')
        .eq('user_id', user.id)
        .single();
      if (data?.first_name) setUserName(data.first_name);
      if (data?.team_id) setTeamId(data.team_id);
    };
    fetchUserProfile();
  }, [user?.id]);

  // Fetch performance data
  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (!user?.id || !teamId) return;
      const now = new Date();
      // Use YYYY-MM-DD format to avoid timezone issues with date-only columns
      const startOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      const endOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;

      const [metricsResult, performancesResult] = await Promise.all([
        supabase
          .from('team_performance_metrics')
          .select('*')
          .eq('team_id', teamId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('team_member_performance')
          .select('*')
          .eq('user_id', user.id)
          .gte('recorded_at', startOfMonth)
          .lte('recorded_at', endOfMonth)
      ]);

      if (metricsResult.data) setPerformanceMetrics(metricsResult.data);
      if (performancesResult.data) setPerformances(performancesResult.data);
    };
    fetchPerformanceData();

    if (!user?.id || !teamId) return;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const channel = supabase
      .channel('performance-realtime-index')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'team_member_performance',
        filter: `user_id=eq.${user.id}`
      }, payload => {
        const newPerformance = payload.new as TeamMemberPerformance;
        if (new Date(newPerformance.recorded_at) >= new Date(startOfMonth)) {
          setPerformances(prev => [...prev, newPerformance]);
        }
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'team_member_performance'
      }, payload => {
        const deletedId = (payload.old as any).id;
        setPerformances(prev => prev.filter(p => p.id !== deletedId));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, teamId]);

  // Load todos
  useEffect(() => {
    if (!user) return;
    const loadTodos = async () => {
      try {
        const { data: todosData, error } = await supabase
          .from('todos')
          .select('*')
          .eq('user_id', user.id)
          .order('due_date', { ascending: true });
        if (error) throw error;
        setTodos(todosData || []);
      } catch (error) {
        console.error('Error loading todos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTodos();

    const channel = supabase
      .channel('todos-changes-index')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'todos',
        filter: `user_id=eq.${user.id}`
      }, () => {
        loadTodos();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Calculate todo statistics
  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const completedTodos = todos.filter(t => t.completed);
  const pendingTodos = todos.filter(t => !t.completed);
  const overdueTodos = pendingTodos.filter(t => t.due_date && getDaysUntilDue(t.due_date)! < 0);

  const currentMonth = new Date().toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long'
  });

  const metricStats = performanceMetrics.map(metric => {
    const metricPerformances = performances.filter(p => p.metric_id === metric.id);
    const count = metricPerformances.length;
    const points = metricPerformances.reduce((sum, p) => sum + p.points, 0);
    return { metric, count, points };
  });

  const totalPerformancePoints = metricStats.reduce((sum, s) => sum + s.points, 0);
  const totalPerformanceCount = metricStats.reduce((sum, s) => sum + s.count, 0);

  // Loading screen - MUST be after all hooks to maintain hooks order
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Lade...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-background min-w-0">
        <AppSidebar />

        <div className="flex-1 flex flex-col min-w-0 max-w-full">
          <header className="h-14 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40 px-4 gap-3">
            <SidebarTrigger />
            <div className="flex-1 min-w-0 flex justify-center">
              <p className="text-sm font-medium truncate">
                {currentTime.getHours() >= 5 && currentTime.getHours() < 12
                  ? 'Guten Morgen'
                  : currentTime.getHours() < 18
                    ? 'Guten Tag'
                    : 'Guten Abend'}
                {userName ? `, ${userName}` : ''}
              </p>
            </div>
            <NotificationBell />
          </header>

          <main className="flex-1 overflow-auto">
            <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

              {/* Shared Dashboard Header - Clock, Timeline, Lead Cards */}
              <DashboardHeader
                currentTime={currentTime}
                todayFollowUps={todayFollowUps}
                globalLeadCounts={globalLeadCounts}
                isLoadingLeadCounts={isLoadingLeadCounts}
                onOpenCalendar={() => setShowCalendarView(true)}
                navigateOnLeadClick={true}
                activeArea={activeArea}
                showActiveAreaButton={true}
              />

              {/* Quick Actions */}
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Schnellzugriff</h2>
                <QuickActions />
              </section>

              {/* Performance Card */}
              <PerformanceCard
                currentMonth={currentMonth}
                metricStats={metricStats}
                totalCount={totalPerformanceCount}
                totalPoints={totalPerformancePoints}
              />

              {/* Todo Overview */}
              <section>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">Aufgaben-Übersicht</h2>
                <TodoOverviewCards
                  total={todos.length}
                  overdue={overdueTodos.length}
                  completed={completedTodos.length}
                  loading={loading}
                />
              </section>

              {/* To-Dos Widget */}
              <HomeTodos />

              {/* Task Widget */}
              <TaskWidget />

            </div>
          </main>
        </div>
      </div>

      {user && (
        <D2DFollowUpCalendar
          open={showCalendarView}
          onOpenChange={setShowCalendarView}
          userId={user.id}
        />
      )}

      <AppAssistant />
    </SidebarProvider>
  );
};

export default Index;
